import { useEffect } from 'react'

/**
 * Per-route SEO updater for the hash-routed SPA.
 *
 * The source HTML uses hash routes (#/services, #/healthcare, ...). The
 * default title + meta description in index.html target the home page;
 * this overlay swaps both on every hashchange so:
 *   - Browser tabs read the active route (UX).
 *   - JS-aware crawlers (Googlebot, Bingbot, Applebot, AI assistants)
 *     pick up route-specific signals when they render the page.
 *
 * Titles are kept under ~60 characters (Google desktop truncation
 * boundary). Descriptions are kept under ~160. Both lead with the
 * route's primary keyword and end with the Pentabay brand to win
 * brand + topic recall in SERPs.
 *
 * Honest scope: hash fragments don't get separate URLs in Google's
 * index - all hash routes are still indexed as https://pentabay.com/.
 * This overlay therefore improves the home page's relevance signals
 * AND the per-tab user experience, but the long-term ranking lift for
 * sub-pages requires path-based routing (a future migration).
 */
const ROUTE_SEO = {
  '/': {
    title: 'Pentabay | Software Development Company in Canada',
    description:
      'Pentabay builds custom software for healthcare, fintech & enterprise clients across Canada and the US. 100+ projects. 5.0 on Clutch.',
  },
  '/services': {
    title: 'Full-Service Software Development Company | Pentabay',
    description:
      'Pentabay is a full-service software development company custom builds, AI & data, cloud & DevOps and product design one compliance-ready delivery team.',
  },
  '/custom-software': {
    title: 'Custom Software Development Company | Pentabay Canada',
    description:
      'Pentabay is a custom software development company delivering web, SaaS & enterprise apps engineered in Canada with compliance-first delivery. Get a fixed-fee quote today.',
  },
  '/ai-data': {
    title: 'AI Software Development Services | AI SaaS & Agents',
    description:
      'AI software development for regulated industries custom AI SaaS, compliant AI agents & data pipelines. See NEO, our HIPAA-aware AI platform built in-house.',
  },
  '/cloud-devops': {
    title: 'Cloud Computing & DevOps Services | AWS, Azure, GCP',
    description:
      'Cloud computing and DevOps services: cloud migration, CI/CD, Kubernetes & infrastructure automation on AWS, Azure & GCP with healthcare-grade security.',
  },
  '/design': {
    title: 'UI UX Design Services for Healthcare, SaaS & Enterprise Products',
    description:
      'UI UX design services for healthcare, fintech, SaaS and enterprise applications across Canada and North America UX research, wireframes, design systems and developer-ready, accessible interfaces.',
  },
  '/industries': {
    title: 'Industries We Serve Healthcare, Fintech, Public Sector | Pentabay',
    description:
      'Pentabay delivers software for healthcare, fintech, public sector and B2B SaaS across Canada and the United States.',
  },
  '/healthcare': {
    title: 'Healthcare Software Development | HIPAA & PHIPA Compliant',
    description:
      'HIPAA & PHIPA compliant healthcare software development medical software, telehealth platforms & EHR integrations built for North American clinics & payers.',
  },
  '/fintech': {
    title: 'FinTech Software Development Company | Pentabay Canada',
    description:
      'Fintech software development company building payments, trading & KYC/AML platforms for fintech startups and neobanks across Canada and the US, SOC 2 & PIPEDA aligned.',
  },
  '/public-sector': {
    title: 'Public Sector Software Development Company | Pentabay',
    description:
      'Public sector software development company delivering ITSG-33 aligned, bilingual-ready government software for Canadian federal, provincial & municipal agencies.',
  },
  '/saas-b2b': {
    title: 'SaaS and B2B product development: new builds and re-platforming',
    description:
      'Pentabay builds software for SaaS and B2B companies multi-tenant architecture, SSO/SCIM, billing & API integrations engineered for scale and enterprise buyers.',
  },
  '/banking-lending': {
    title: 'Banking Software Development Company | Pentabay',
    description:
      'Banking software development company delivering core banking, lending & KYC platforms aligned to OSFI B-13 and SOC 2 for Canadian and US financial institutions.',
  },
  '/telecommunications': {
    title: 'Telecom Software Development Company | Pentabay',
    description:
      'Telecom software development company building OSS/BSS platforms, billing systems & network integrations for telecommunications providers across North America.',
  },
  '/ai-edtech': {
    title: 'EdTech Software Development Company | Pentabay',
    description:
      'EdTech software development company building AI-powered learning platforms, LMS integrations & adaptive learning tools for education and training providers.',
  },
  '/real-estate': {
    title: 'Real Estate Software Development Services for Property Managers & PropTech Companies',
    description:
      'Real estate software development for property managers and PropTech teams tenant portals, property listings, lease and maintenance workflows, and recurring rent collection in one platform.',
  },
  '/trust': {
    title: 'SOC 2 Security Architecture & Secure SDLC | Pentabay Trust',
    description:
      'How Pentabay engineers trust: data residency, access control, secure SDLC and continuous SOC 2 & PIPEDA-aligned compliance posture across every engagement.',
  },
  '/compliance': {
    title: 'PHIPA & HIPAA Compliance Software Development | Pentabay',
    description:
      'Pentabay engineers PHIPA & HIPAA compliant healthcare software encryption, access controls, audit trails and compliance documentation built into every product we ship.',
  },
  '/insights': {
    title: 'Newsroom | Pentabay in the Press & Public Listings',
    description:
      'Verified public listings and press mentions for Pentabay Inc. on Clutch, LinkedIn, Crunchbase and SoftwareWorld independently checkable, no filler.',
  },
  '/about': {
    title: 'About Pentabay | Custom Software Company, Milton, Ontario',
    description:
      'Pentabay Inc., founded 2018 in Milton, Ontario, builds custom healthcare, fintech and SaaS software across Canada and the US. 100+ projects shipped.',
  },
  '/careers': {
    title: 'Careers at Pentabay | Software Jobs, Milton & India',
    description:
      'Join Pentabay\'s engineering studio in Milton, Ontario and India. Open roles in custom software, cloud/DevOps, AI and product design for healthcare clients.',
  },
  '/hr-forms': {
    title: 'Candidate Recruitment Form | Pentabay',
    description:
      'Apply to open roles at Pentabay. Submit your resume, experience, and role preferences through our HR recruitment form.',
    // Reachable via direct/shared link, deliberately excluded from search
    // results - it's an application form, not a page meant to rank.
    noindex: true,
  },
  '/weguard': {
    title: 'WeGuard Continuous Security & Compliance for Healthcare Clouds',
    description:
      'WeGuard by Pentabay: continuous security and compliance posture for healthcare clouds controls, evidence and audit, in one platform.',
    favicon: '/assets/icon-weguard.webp',
  },
  '/contact-us': {
    title: 'Contact Pentabay | Get a Custom Software Quote',
    description:
      'Tell Pentabay what you\'re building. We respond within one business day across Canada and the US. Email contact@pentabay.com for a free quote.',
  },
  '/phipa-vs-hipaa-canadian-healthcare-software-compliance': {
    title: 'PHIPA vs. HIPAA: A Real Comparison for Healthcare Software',
    description:
      'PHIPA, HIPAA, and PIPEDA explained in plain language what actually differs, what overlaps, and what compliance-literate software looks like in 2026.',
  },
  '/ai-data-compliant-ai-canada': {
    title: 'What "AI & Data" Actually Means for a Regulated Business',
    description:
      'No AI law exists in Canada yet here\'s what "compliant AI" actually requires right now for healthcare, fintech, and public-sector builds in 2026.',
  },
  '/product-design-compliance-decision-aoda': {
    title: 'Product Design Is a Compliance Decision, Not Just Aesthetic',
    description:
      'AODA\'s next reporting deadline is December 31, 2026 what Ontario businesses actually need to know about accessible design before then.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Pentabay',
    description:
      'How Pentabay collects, uses and protects information shared through pentabay.com. Plain language privacy policy for visitors in Canada, the US and globally.',
  },
  '/cookies-policy': {
    title: 'Cookies & Storage Policy | Pentabay',
    description:
      'What client-side storage pentabay.com sets in your browser, why, and how to control it. No tracking cookies. ePrivacy and PIPEDA-aligned disclosure.',
  },
  '/our-clients': {
    title: 'Our Clients | Capital One, Verizon & More Pentabay',
    description:
      'Pentabay clients include Capital One, Verizon Wireless, Comcast, AFS Vision and MD Connected. 100+ projects, 20+ clients, 5.0/5 across 6 Clutch reviews.',
  },
  '/our-work': {
    title: 'Our Work | Custom Software Case Studies Pentabay',
    description:
      'Five Clutch-verified Pentabay case studies: telehealth, AI/EdTech, real estate and fintech projects, with scope, tech stack, timeline and outcomes.',
  },
  '/cloud-service': {
    title: 'Managed Cloud Services & Support | Pentabay Cloud',
    description:
      'Managed cloud services with 24/7 cross-time-zone support cost optimization, monitoring & compliance posture management for AWS, Azure & GCP workloads.',
  },
  '/ava': {
    title: 'AVA Team Communication Platform | Pentabay',
    description:
      'AVA is Pentabay’s team-communication platform for clinics: real-time channels, end-to-end audited message retention, PHI-aware by default. Web, iOS, Android.',
    favicon: '/assets/icon-ava.webp',
  },
  '/zentra': {
    title: 'Zentra HR Management Platform | Pentabay',
    description:
      'Zentra is Pentabay’s HR management platform for regulated workforces. Employees, payroll, attendance, credentialing and compliance reporting built for privacy.',
    favicon: '/assets/icon-zentra.webp',
  },
  '/mydart': {
    title: 'MyDart All-in-One SEO & Digital Marketing Platform | Pentabay',
    description:
      'Rank tracking, social media scheduling, email campaigns and an AI SEO agent in one dashboard. Free plan to evaluate; Pro at $49/mo, Enterprise at $149/mo.',
    favicon: '/assets/icon-mydart.webp',
  },
  '/neo': {
    title: 'NEO AI Voice Agents for Healthcare (HIPAA & PHIPA) | Pentabay',
    description:
      'AI-powered virtual receptionist for healthcare. HIPAA and PHIPA compliant voice agents with real-time analytics, call transcription, smart routing and 24/7 availability.',
    favicon: '/assets/icon-neo.webp',
  },
  '/saas-platforms': {
    title: 'SaaS Platform Development Services | Pentabay',
    description:
      'SaaS platform development services covering multi-tenant architecture, RBAC, billing & AWS infrastructure built to scale from MVP to enterprise-grade product.',
  },
  '/mobile-development': {
    title: 'Mobile App Development Company | iOS & Android Experts',
    description:
      'Mobile app development company building secure iOS, Android & cross-platform (React Native/Flutter) apps for healthcare, fintech & field teams engineered in Canada, HIPAA-ready.',
  },
  /* Renamed from /web-marketing 2026-09-21. The page BODY is unchanged - it
     still sells CMS builds and web performance work - so the description
     continues to describe that. Only the title carries the new practice name.
     The old slug 301s here (GONE map in Pentabay.html, plus the CloudFront
     function in infra/). */
  '/digital-marketing': {
    title: 'Canadian Digital Marketing Agency | SEO & PPC | Pentabay',
    description:
      'Canadian digital marketing agency offering SEO, Google Ads, social media, content and analytics to help businesses attract and convert more customers.',
  },
  '/seo-services': {
    title: 'SEO Company in Canada | Technical & Local SEO | Pentabay',
    description:
      'SEO company in Canada providing technical SEO, local SEO, keyword research and audits to improve search visibility and attract qualified traffic.',
  },
  '/ppc-google-ads': {
    title: 'PPC Management & Google Ads Services Canada | Pentabay',
    description:
      'Google Ads and PPC management in Canada focused on qualified traffic, better conversions and clear campaign performance for growing businesses.',
  },
  '/cybersecurity': {
    title: 'Cybersecurity Services & Consulting in Canada | Pentabay',
    description:
      'Cybersecurity services in Canada for assessments, managed security, penetration testing and compliance support to help businesses reduce cyber risk.',
  },
  '/cybersecurity-assessment': {
    title: 'Cybersecurity Assessment Services Canada | Pentabay',
    description:
      'Cybersecurity assessment services in Canada to identify security gaps, assess risks and prioritize practical improvements across your business.',
  },
  '/managed-cybersecurity': {
    title: 'Managed Cybersecurity Services in Canada | Pentabay',
    description:
      'Managed cybersecurity services in Canada with ongoing monitoring, security reviews and practical support to help businesses reduce cyber risk.',
  },
  '/penetration-testing': {
    title: 'Penetration Testing & Vulnerability Assessment',
    description:
      'Penetration testing and vulnerability assessment for web, API, cloud and mobile, reported as reproducible defects your engineers can act on.',
  },
  '/low-code-platforms': {
    title: 'Low-Code App Development Services | Pentabay',
    description:
      'Low-code app development services on Appian, Zoho, Xano & Creatio faster delivery for internal tools and customer-facing apps without sacrificing control.',
  },
  '/enterprise-it': {
    title: 'IT Consulting Services for Enterprise Cloud Modernization',
    description:
      'IT consulting services for enterprise cloud modernization cloud migration, DevOps, infrastructure modernization and managed operations delivered by one engineering team.',
  },
  '/software-development-toronto': {
    title: 'Software Development in Toronto & the GTA | Pentabay',
    description:
      'Custom software development in Toronto and the GTA by Pentabay a Milton, Ontario team building healthcare, fintech, SaaS and AI software on Eastern time. Get a quote.',
  },
  '/software-development-canada': {
    title: 'Canadian Software Development Company Coast to Coast | Pentabay',
    description:
      'Pentabay is a Canadian software development company (since 2018, HQ in Milton, Ontario). PIPEDA, PHIPA and Law 25 compliant, with data residency in Canada and CAD billing.',
  },
  '/software-development-usa': {
    title: 'Software Development for U.S. Companies HIPAA & SOC 2 | Pentabay',
    description:
      'Custom software development for U.S. businesses by Pentabay HIPAA and SOC 2 aligned, in overlapping U.S. hours. Trusted by Capital One, Verizon and Comcast.',
  },
  '/blog': {
    title: 'Blog | Software Engineering & Compliance Insights Pentabay',
    description:
      'Practitioner insights on healthcare compliance (PHIPA vs HIPAA), custom software costs in Canada, and AI/cloud engineering from Pentabay engineers.',
  },
  '/healthcare-software-development-guide': {
    title: 'Healthcare Software Development: The 2026 Guide | Pentabay',
    description:
      'A practical guide to healthcare software development in Canada and the U.S. HIPAA, PHIPA and PIPEDA, HL7/FHIR interoperability, architecture, cost and timeline.',
  },
  '/fhir-hl7-integration-guide': {
    title: 'FHIR & HL7 Integration Guide for Builders | Pentabay',
    description:
      'HL7 v2 vs FHIR, the resources and messages that matter, interface engines, and the integration pitfalls that sink healthcare projects a practical guide from Pentabay.',
  },
  '/custom-software-development-cost-canada': {
    title: 'How Much Does Custom Software Cost in Canada? (2026)',
    description:
      'Real 2026 Canadian custom software pricing by project type, compliance load, and engagement model plus what actually moves your quote up or down.',
  },
  '/soc-2-ready-vendor-guide': {
    title: 'What a SOC 2-Ready Vendor Actually Looks Like',
    description:
      "SOC 2 isn't a single badge here's what Trust Services Categories, report types, and 2022's updated criteria actually mean for buyers.",
  },
  '/public-sector-software-buyers': {
    title: 'What Public Sector Software Buyers Actually Look For',
    description:
      'Government software procurement runs on formal process, documented security, accessibility obligations and data residency what really shapes the decision.',
  },
  '/b2b-saas-what-enterprise-buyers-actually-check': {
    title: 'What Enterprise Buyers Actually Check Before Signing',
    description:
      'SSO, security questionnaires, and vendor risk reviews - what actually happens before an enterprise buyer signs off on B2B SaaS.',
  },
  '/banking-lending-software-core-system-integration': {
    title: 'Banking & Lending Software: What Actually Gets Hard',
    description:
      'Credit decisioning, core banking integration, and legacy system constraints - what actually makes banking and lending software difficult.',
  },
  '/telecommunications-software-what-actually-breaks': {
    title: 'Telecom Software: What Actually Breaks at Scale',
    description:
      'Billing accuracy, real-time network data, and legacy integration - what actually makes telecommunications software difficult to build well.',
  },
  '/agentic-ai-regulated-industries-what-actually-changes': {
    title: 'Agentic AI in Regulated Industries: What Actually Changes',
    description:
      'Human-approval boundaries, auditability, and reversibility - what actually changes when an AI system takes actions on its own inside a regulated workflow.',
  },
  '/shadow-ai-compliance-risk': {
    title: 'Shadow AI: The Compliance Risk Hiding in Plain Sight',
    description:
      "Employees using unapproved AI tools with company data isn't a hypothetical risk - it's already happening, and it's a real compliance exposure.",
  },
  '/remote-patient-monitoring-what-software-needs-to-handle': {
    title: 'Remote Patient Monitoring: What Software Needs to Handle',
    description:
      "Continuous patient data from home devices raises different engineering questions than a single office visit - here's what actually matters.",
  },
  '/ai-symptom-checkers-triage-what-the-line-actually-is': {
    title: 'AI Symptom Checkers: Where the Regulatory Line Actually Is',
    description:
      "Patient-facing AI triage tools sit close to a real regulatory line - here's what separates a helpful tool from a medical device.",
  },
  '/digital-front-door-healthcare-what-it-actually-requires': {
    title: 'Digital Front Door: What Healthcare Software Actually Needs',
    description:
      "Scheduling, messaging, billing, and telehealth in one place sounds simple - here's what actually makes a digital front door work.",
  },
  '/population-health-management-what-actually-matters': {
    title: 'Population Health Management: What Actually Matters',
    description:
      'Value-based care shifts the question from "is this patient sick" to "which patients are trending toward sick" - what that requires technically.',
  },
  '/prior-authorization-automation-what-actually-changes': {
    title: 'Prior Authorization Automation: What Actually Changes',
    description:
      'Manual prior auth is being replaced by API-based systems - what that actually requires, and where the real complexity hides.',
  },
  '/sdoh-data-integration-what-actually-matters': {
    title: 'Social Determinants of Health: What Software Needs to Do',
    description:
      "Housing, transportation, and food access predict health outcomes as much as clinical data - here's what SDOH software actually requires.",
  },
  '/ransomware-as-a-service-why-skill-barrier-dropped': {
    title: 'Ransomware-as-a-Service: The Skill Barrier Just Dropped',
    description:
      "Ransomware no longer requires technical skill to deploy - here's what the RaaS business model actually changed, and why backups aren't enough.",
  },
  '/real-estate-proptech-what-software-actually-needs-to-do': {
    title: 'PropTech Software: What It Actually Needs to Get Right',
    description:
      'MLS integration, listing data, and tenant systems - what real estate and property management software actually needs to handle well.',
  },
  '/ai-edtech-student-data-privacy-ferpa-coppa': {
    title: 'AI in EdTech: What FERPA and COPPA Actually Require',
    description:
      "Student data privacy isn't optional in edtech - what FERPA and COPPA actually require, and where AI features complicate compliance.",
  },
  '/enterprise-it-legacy-modernization': {
    title: 'Legacy System Modernization: What Actually Works',
    description:
      'Rip-and-replace versus phased modernization what actually determines the right approach for aging enterprise IT systems.',
  },
  '/low-code-no-code-when-it-actually-works': {
    title: "Low-Code and No-Code: When It Actually Works and Doesn't",
    description:
      "Low-code and no-code platforms solve real problems and create real limits here's where each one actually applies.",
  },
  '/web-marketing-sites-what-actually-matters': {
    title: 'What Actually Matters in a Marketing Website Build',
    description:
      'CMS choice, performance, and crawlability what separates a marketing site that converts from one that just looks good.',
  },
  '/mobile-development-native-vs-cross-platform': {
    title: 'Native vs Cross-Platform Mobile Development, Explained',
    description:
      'What actually separates native from cross-platform mobile development, and when each one is genuinely the right call for your product.',
  },
  '/cloud-devops-what-it-actually-includes': {
    title: 'What "Cloud & DevOps" Actually Includes as a Service',
    description:
      'Cloud & DevOps means more than "we use AWS" infrastructure, automation, security, and what to actually ask a vendor about.',
  },
  '/how-to-choose-a-software-development-company': {
    title: 'How to Choose a Software Development Company | Pentabay',
    description:
      'A practical guide to choosing a software development company: the criteria that predict success, the red flags to avoid, and the questions to ask first.',
  },
  '/custom-software-vs-off-the-shelf': {
    title: 'Custom Software vs Off-the-Shelf: How to Decide | Pentabay',
    description:
      'Off-the-shelf SaaS or custom-built? An honest build-vs-buy decision framework covering fit, integration, compliance and total cost of ownership.',
  },
  '/offshore-vs-nearshore-software-development': {
    title: 'Offshore vs Nearshore Development | Pentabay',
    description:
      'Offshore vs nearshore software development for US and Canadian buyers: compare time-zone overlap, IP, cost, and quality control honestly.',
  },
  '/healthcare-software-vendor-checklist': {
    title: 'Healthcare Software Vendor Selection Checklist | Pentabay',
    description:
      'A 16-point checklist to evaluate a healthcare software vendor: HIPAA/PHIPA alignment, FHIR/HL7, SOC 2, data residency, delivery, references and IP ownership.',
  },
  // ---- Networking & Cybersecurity, 2026-10-12 (DM team content brief) ----
  '/networking': {
    title: 'IT Networking Services for Businesses in Canada | Pentabay',
    description:
      'Pentabay provides IT networking services for Canadian businesses, including network design, infrastructure, Wi-Fi, switching, secure connectivity and ongoing support.',
  },
  '/network-installation': {
    title: 'Network Installation Services for Businesses | Pentabay',
    description:
      'Professional network installation services for offices and commercial locations including Wi-Fi, managed switches, VLANs and secure network configuration.',
  },
  '/network-maintenance': {
    title: 'Network Support Services & Managed Networks | Pentabay',
    description:
      'Business network support services including monitoring, troubleshooting, Wi-Fi and switch support, firewall management, updates and ongoing maintenance.',
  },
  '/network-security': {
    title: 'Business Network Security, VPN & Wi-Fi Services | Pentabay',
    description:
      'Secure business networks with firewall installation, VPN, Wi-Fi security, network segmentation, secure remote access and Zero Trust solutions.',
  },
  '/network-equipment': {
    title: 'Business Network Equipment & Deployment | Pentabay',
    description:
      'Pentabay helps businesses source, configure and deploy firewalls, managed switches, Wi-Fi access points, network racks and supporting equipment.',
  },
  '/nord-solutions': {
    title: 'Nord Security Solutions for Business | Pentabay',
    description:
      'Secure business identities, access and external threat visibility with NordPass, NordLayer and NordStellar solutions implemented by Pentabay.',
  },
  '/security-implementation': {
    title: 'Security Solution Implementation & Support | Pentabay',
    description:
      'Pentabay helps businesses assess, configure, migrate, onboard and manage Nord security solutions including NordPass, NordLayer and NordStellar.',
  },
  '/security-packages': {
    title: 'Business Security Packages | Nord Solutions + Pentabay',
    description:
      'Compare Pentabay security packages combining NordPass, NordLayer, NordStellar, implementation and ongoing support for business security needs.',
  },
}

// The product-page favicons ship in /public/assets. Every other route
// keeps the corporate Pentabay mark declared in index.html.
const DEFAULT_FAVICON = '/assets/pentabay-mark.png'

// ---- Breadcrumbs -----------------------------------------------------
// Short, human labels per route for the BreadcrumbList schema (falls back
// to a title-cased slug for any route not listed). Service and industry
// detail pages nest under their hub (/services, /industries); everything
// else is a direct child of Home.
const CRUMB_NAME = {
  '/services': 'Services',
  '/custom-software': 'Custom Software',
  '/ai-data': 'AI & Data',
  '/cloud-devops': 'Cloud & DevOps',
  '/cloud-service': 'Cloud Services',
  '/design': 'Design',
  '/mobile-development': 'Mobile Development',
  '/saas-platforms': 'SaaS Platforms',
  '/digital-marketing': 'Digital Marketing',
  '/seo-services': 'SEO Services',
  '/ppc-google-ads': 'PPC / Google Ads',
  '/cybersecurity': 'Cybersecurity',
  '/cybersecurity-assessment': 'Cybersecurity Assessment',
  '/managed-cybersecurity': 'Managed Cybersecurity',
  '/penetration-testing': 'Penetration Testing',
  '/low-code-platforms': 'Low-code / No-code',
  '/industries': 'Industries',
  '/healthcare': 'Healthcare',
  '/fintech': 'Fintech',
  '/public-sector': 'Public Sector',
  '/banking-lending': 'Banking & Lending',
  '/telecommunications': 'Telecommunications',
  '/ai-edtech': 'AI & EdTech',
  '/saas-b2b': 'SaaS & B2B',
  '/real-estate': 'Real Estate',
  '/enterprise-it': 'Enterprise IT',
  '/trust': 'Trust',
  '/compliance': 'Compliance',
  '/insights': 'Newsroom',
  '/about': 'About',
  '/careers': 'Careers',
  '/hr-forms': 'Candidate Recruitment Form',
  '/contact-us': 'Contact',
  '/privacy-policy': 'Privacy Policy',
  '/cookies-policy': 'Cookies Policy',
  '/our-clients': 'Our Clients',
  '/our-work': 'Our Work',
  '/software-development-toronto': 'Toronto & the GTA',
  '/software-development-canada': 'Canada',
  '/software-development-usa': 'United States',
  '/phipa-vs-hipaa-canadian-healthcare-software-compliance': 'PHIPA vs. HIPAA',
  '/ai-data-compliant-ai-canada': 'Compliant AI in Canada',
  '/product-design-compliance-decision-aoda': 'Product Design & Compliance',
  '/blog': 'Blog',
  '/healthcare-software-development-guide': 'Healthcare Software Guide',
  '/fhir-hl7-integration-guide': 'FHIR & HL7 Integration Guide',
  '/custom-software-development-cost-canada': 'Custom Software Cost (Canada)',
  '/soc-2-ready-vendor-guide': 'SOC 2-Ready Vendors',
  '/public-sector-software-buyers': 'Public Sector Buyers',
  '/low-code-no-code-when-it-actually-works': 'Low-Code vs No-Code',
  '/mobile-development-native-vs-cross-platform': 'Native vs Cross-Platform',
  '/web-marketing-sites-what-actually-matters': 'Marketing Website Builds',
  '/cloud-devops-what-it-actually-includes': 'What Cloud & DevOps Includes',
  '/enterprise-it-legacy-modernization': 'Legacy System Modernization',
  '/ai-edtech-student-data-privacy-ferpa-coppa': 'AI in EdTech: FERPA & COPPA',
  '/real-estate-proptech-what-software-actually-needs-to-do': 'PropTech Software',
  '/b2b-saas-what-enterprise-buyers-actually-check': 'Enterprise Buyer Checks',
  '/banking-lending-software-core-system-integration': 'Banking & Lending Software',
  '/telecommunications-software-what-actually-breaks': 'Telecom Software at Scale',
  '/agentic-ai-regulated-industries-what-actually-changes': 'Agentic AI in Regulated Industries',
  '/shadow-ai-compliance-risk': 'Shadow AI Compliance Risk',
  '/prior-authorization-automation-what-actually-changes': 'Prior Authorization Automation',
  '/remote-patient-monitoring-what-software-needs-to-handle': 'Remote Patient Monitoring',
  '/ai-symptom-checkers-triage-what-the-line-actually-is': 'AI Symptom Checkers',
  '/digital-front-door-healthcare-what-it-actually-requires': 'Digital Front Door',
  '/population-health-management-what-actually-matters': 'Population Health',
  '/sdoh-data-integration-what-actually-matters': 'Social Determinants of Health',
  '/ransomware-as-a-service-why-skill-barrier-dropped': 'Ransomware-as-a-Service',
  '/how-to-choose-a-software-development-company': 'Choosing a Dev Company',
  '/custom-software-vs-off-the-shelf': 'Custom vs Off-the-Shelf',
  '/offshore-vs-nearshore-software-development': 'Offshore vs Nearshore',
  '/healthcare-software-vendor-checklist': 'Vendor Checklist',
  '/networking': 'Networking',
  '/network-installation': 'Network Installation & Configuration',
  '/network-maintenance': 'Network Support & Maintenance',
  '/network-security': 'Wi-Fi, VPN & Network Security',
  '/network-equipment': 'Network Equipment',
  '/nord-solutions': 'Nord Solutions',
  '/security-implementation': 'Implementation',
  '/security-packages': 'Packages',
}

/* Service pages that sit UNDER another service page rather than directly
   under the /services hub. Added 2026-09-21 with the Digital Marketing and
   Cybersecurity families.

   The site's URLs are flat - the router in Pentabay.html only matches a
   single path segment, so /digital-marketing/seo-services cannot exist as a
   URL. The hierarchy is therefore carried entirely by this map, which turns
   the trail from three levels into four:

     Home > Services > Digital Marketing > SEO Services

   Keep in sync with the identical map in the other file (this constant is
   duplicated in RouteSEO.jsx and Breadcrumbs.jsx for the same reason
   CRUMB_NAME and SERVICE_ROUTES already are - the two modules must not
   import from each other, because generate-seo-html.mjs parses RouteSEO.jsx
   as a standalone text file at build time and cannot resolve imports). */
const SERVICE_PARENT = {
  '/seo-services': '/digital-marketing',
  '/ppc-google-ads': '/digital-marketing',
}

const SERVICE_ROUTES = new Set([
  '/custom-software', '/ai-data', '/cloud-devops', '/cloud-service', '/design',
  '/mobile-development', '/saas-platforms', '/digital-marketing', '/low-code-platforms',
  '/seo-services', '/ppc-google-ads',
])

/* Networking, Cyber Security and Solutions pages behind the dedicated
   "Networking & Cybersecurity" top-level nav item (Pentabay.html,
   2026-10-12) - NOT a Services child, so these deliberately do not go
   through SERVICE_ROUTES/SERVICE_PARENT above. /cybersecurity used to:
   its nav link lived inside the Services dropdown until this change moved
   it into the new menu, which made "Home > Services > Cybersecurity" a
   stale trail (Services no longer leads there). Same duplication contract
   as SERVICE_PARENT: kept in sync with the identical map in Breadcrumbs.jsx. */
const NETSEC_PARENT = {
  '/cybersecurity-assessment': '/cybersecurity',
  '/managed-cybersecurity': '/cybersecurity',
  '/penetration-testing': '/cybersecurity',
  '/network-installation': '/networking',
  '/network-maintenance': '/networking',
  '/network-security': '/networking',
  '/network-equipment': '/networking',
  '/security-implementation': '/nord-solutions',
  '/security-packages': '/nord-solutions',
}

const INDUSTRY_ROUTES = new Set([
  '/healthcare', '/fintech', '/public-sector', '/banking-lending',
  '/telecommunications', '/ai-edtech', '/saas-b2b', '/real-estate', '/enterprise-it',
])
// Blog articles nest under the /blog hub in the breadcrumb trail.
const BLOG_ROUTES = new Set([
  '/healthcare-software-development-guide', '/fhir-hl7-integration-guide',
  '/custom-software-development-cost-canada', '/soc-2-ready-vendor-guide',
  '/how-to-choose-a-software-development-company',
  '/custom-software-vs-off-the-shelf', '/offshore-vs-nearshore-software-development',
  '/healthcare-software-vendor-checklist', '/ai-data-compliant-ai-canada',
  '/product-design-compliance-decision-aoda', '/public-sector-software-buyers',
  '/cloud-devops-what-it-actually-includes',
  '/low-code-no-code-when-it-actually-works',
  '/enterprise-it-legacy-modernization',
  '/ai-edtech-student-data-privacy-ferpa-coppa',
  '/real-estate-proptech-what-software-actually-needs-to-do',
  '/mobile-development-native-vs-cross-platform',
  '/web-marketing-sites-what-actually-matters',
  '/b2b-saas-what-enterprise-buyers-actually-check',
  '/banking-lending-software-core-system-integration',
  '/telecommunications-software-what-actually-breaks',
  '/agentic-ai-regulated-industries-what-actually-changes',
  '/shadow-ai-compliance-risk',
  '/prior-authorization-automation-what-actually-changes',
  '/remote-patient-monitoring-what-software-needs-to-handle',
  '/ai-symptom-checkers-triage-what-the-line-actually-is',
  '/digital-front-door-healthcare-what-it-actually-requires',
  '/population-health-management-what-actually-matters',
  '/sdoh-data-integration-what-actually-matters',
  '/ransomware-as-a-service-why-skill-barrier-dropped',
])

/* Separately deployed products living in their own S3 prefixes - never
   built from this repo, never in ROUTE_SEO. In production these paths are
   served directly by CloudFront/S3 and never reach this React app at all
   (see the comment on App() in main.jsx); this list exists so isKnownRoute()
   below gives the same answer in local dev, where there is no CloudFront in
   front to intercept them first. Kept in sync with FOREIGN_PREFIXES in
   scripts/generate-seo-html.mjs and the literal list in main.jsx's comment -
   same duplication reason as CRUMB_NAME/SERVICE_ROUTES above. */
const PRODUCT_APP_PREFIXES = ['/weguard', '/neo', '/mydart', '/ava', '/zentra', '/trackme']

/**
 * Single source of truth for "does this app have a real page at this path",
 * shared by main.jsx (decide HtmlPage vs NotFound) and RouteSEO's own apply()
 * below (decide real SEO tags vs 404 tags - see the comment there for why
 * that second use matters just as much as the first). Added 2026-09-30: the
 * router previously had no such concept, so an unrecognised path silently
 * rendered the homepage's own content, title and canonical - a soft 404
 * indistinguishable from the real page even to search engines, which a DM
 * team technical SEO audit flagged directly.
 *
 * Accepts a raw pathname as-is (e.g. straight from useLocation()) - a single
 * trailing slash is stripped first, since '/about/' has to answer the same
 * as '/about' here. Without that, this path would incorrectly show NotFound
 * for the trailing-slash form of every legitimate route, which is very much
 * still a real page (see infra/spa-rewrite.cloudfront-function.js - it 301s
 * to the bare form in production, it doesn't mean the page doesn't exist).
 */
export function isKnownRoute(route) {
  if (route === '/') return true
  const path = route.length > 1 && route.endsWith('/') ? route.slice(0, -1) : route
  if (path === '/') return true
  if (Object.prototype.hasOwnProperty.call(ROUTE_SEO, path)) return true
  return PRODUCT_APP_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))
}

function crumbName(route) {
  if (route === '/') return 'Home'
  if (CRUMB_NAME[route]) return CRUMB_NAME[route]
  return route.replace(/^\//, '').split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

function normaliseHash() {
  const raw = window.location.hash || ''
  if (!raw || raw === '#' || raw === '#/') return '/'
  let path = raw.startsWith('#') ? raw.slice(1) : raw
  if (path.startsWith('/')) path = path
  else path = '/' + path
  const queryIdx = path.indexOf('#')
  if (queryIdx !== -1) path = path.slice(0, queryIdx)
  return path || '/'
}

/**
 * Resolve the active route. Since the 2026-05 migration to clean /route
 * URLs the route lives in location.pathname, not the hash - so read the
 * pathname first and only fall back to the legacy hash form for old
 * bookmarks/links. (Previously this read the hash exclusively, which was
 * always empty on path routes and collapsed every sub-page's SEO to the
 * home-page defaults.)
 */
function currentRoute() {
  const path = window.location.pathname || '/'
  const m = path.match(/^\/([a-z0-9-]+)\/?$/i)
  if (m && m[1]) return '/' + m[1]
  return normaliseHash()
}

function ensureMeta(selector, attr, attrName, attrValue) {
  let el = document.querySelector(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, attrName)
    document.head.appendChild(el)
  }
  el.setAttribute('content', attrValue)
}

function ensureCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/* A 404 has no authoritative URL to point a canonical at - Google's own
   guidance is to omit the tag entirely on an error page rather than self-
   reference the broken URL or (the bug this fixes) silently inherit
   whatever page set the tag last. */
function removeCanonical() {
  const el = document.querySelector('link[rel="canonical"]')
  if (el) el.remove()
}

/**
 * Point the tab/bookmark favicon at `href`. Updates every icon link
 * declared in index.html (icon, shortcut icon, apple-touch-icon) so the
 * product pages (AVA, Zentra, MyDart, WeGuard, NEO) show their own mark
 * and non-product routes fall back to the corporate Pentabay logo.
 */
function ensureFavicon(href) {
  // Safari ignores in-place href edits on an existing <link rel="icon"> - it
  // reads the favicon once and keeps it. Removing the icon links and
  // appending fresh nodes forces every browser (Safari included) to re-read
  // the favicon for the current route. The static /favicon.ico remains the
  // zero-JS fallback the browser fetches from the site root regardless.
  document
    .querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.remove())

  const icon = document.createElement('link')
  icon.setAttribute('rel', 'icon')
  icon.setAttribute('type', href.endsWith('.png') ? 'image/png' : 'image/x-icon')
  icon.setAttribute('href', href)
  document.head.appendChild(icon)

  const apple = document.createElement('link')
  apple.setAttribute('rel', 'apple-touch-icon')
  apple.setAttribute('href', href)
  document.head.appendChild(apple)

  const shortcut = document.createElement('link')
  shortcut.setAttribute('rel', 'shortcut icon')
  shortcut.setAttribute('href', '/favicon.ico')
  document.head.appendChild(shortcut)
}

/* ---- Service schema ------------------------------------------------
 * Per-page Service JSON-LD, added 2026-09-21 for the Digital Marketing and
 * Cybersecurity families.
 *
 * index.html's static @graph already declares the Organization and an
 * OfferCatalog of broad service categories. That is company-level markup and
 * it stays as it is; this emits a page-level Service node instead, so a
 * search engine landing on /penetration-testing sees a Service whose URL is
 * that page rather than only a catalogue entry on the homepage.
 *
 * `provider` references the Organization by @id rather than restating it, so
 * there is one Organization in the graph and no risk of the two copies
 * disagreeing. Descriptions are read from ROUTE_SEO rather than duplicated -
 * the meta description and the schema description should not be able to
 * drift apart.
 *
 * Hub pages list their children through hasOfferCatalog, which is the same
 * pattern index.html uses; children point back with isRelatedTo. Together
 * those express the parent/child relationship the flat URLs cannot.
 *
 * Scope note: deliberately limited to the seven pages of this family. The
 * older service routes are covered by the homepage OfferCatalog and adding
 * them here is a separate, larger change.
 */
const SERVICE_SCHEMA = {
  '/digital-marketing': {
    name: 'Digital Marketing Services',
    serviceType: 'Digital marketing',
    children: ['/seo-services', '/ppc-google-ads'],
  },
  '/seo-services': {
    name: 'SEO Services',
    serviceType: 'Search engine optimisation',
    parent: '/digital-marketing',
  },
  '/ppc-google-ads': {
    name: 'PPC and Google Ads Management',
    serviceType: 'Paid search advertising',
    parent: '/digital-marketing',
  },
  '/cybersecurity': {
    name: 'Cybersecurity Services',
    serviceType: 'Cybersecurity',
    children: ['/cybersecurity-assessment', '/managed-cybersecurity', '/penetration-testing'],
  },
  '/cybersecurity-assessment': {
    name: 'Cybersecurity Assessment',
    serviceType: 'Security risk assessment',
    parent: '/cybersecurity',
  },
  '/managed-cybersecurity': {
    name: 'Managed Cybersecurity Services',
    serviceType: 'Managed security services',
    parent: '/cybersecurity',
  },
  '/penetration-testing': {
    name: 'Penetration Testing and Vulnerability Assessment',
    serviceType: 'Penetration testing',
    parent: '/cybersecurity',
  },
}

const SERVICE_SCHEMA_ID = 'pb-service-schema'

function ensureServiceSchema(route) {
  const cfg = SERVICE_SCHEMA[route]
  const existing = document.getElementById(SERVICE_SCHEMA_ID)
  if (!cfg) {
    if (existing) existing.remove()
    return
  }
  const url = `https://pentabay.com${route}`
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${url}#service`,
    name: cfg.name,
    serviceType: cfg.serviceType,
    description: (ROUTE_SEO[route] || {}).description,
    url,
    provider: { '@id': 'https://pentabay.com/#organization' },
    areaServed: [
      { '@type': 'Country', name: 'Canada' },
      { '@type': 'Country', name: 'United States' },
    ],
  }
  if (cfg.children && cfg.children.length) {
    data.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      name: `${cfg.name} — specialisms`,
      itemListElement: cfg.children.map((c) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          '@id': `https://pentabay.com${c}#service`,
          name: (SERVICE_SCHEMA[c] || {}).name || crumbName(c),
          url: `https://pentabay.com${c}`,
        },
      })),
    }
  }
  if (cfg.parent) {
    data.isRelatedTo = {
      '@type': 'Service',
      '@id': `https://pentabay.com${cfg.parent}#service`,
      name: (SERVICE_SCHEMA[cfg.parent] || {}).name || crumbName(cfg.parent),
      url: `https://pentabay.com${cfg.parent}`,
    }
  }
  const el = existing || document.createElement('script')
  el.id = SERVICE_SCHEMA_ID
  el.setAttribute('type', 'application/ld+json')
  el.textContent = JSON.stringify(data)
  if (!existing) document.head.appendChild(el)
}

/**
 * Emit (or refresh) a BreadcrumbList JSON-LD for the current route so
 * search engines can render breadcrumb rich results. Trail is
 * Home → [Services|Industries hub] → [parent service] → current page. The
 * home route gets no breadcrumb (a single-item trail adds no value). Kept in
 * its own <script data-breadcrumb> so it never clobbers the static @graph in
 * index.html.
 */
function ensureBreadcrumbs(route) {
  const existing = document.querySelector('script[data-breadcrumb]')
  if (route === '/') {
    if (existing) existing.remove()
    return
  }
  const trail = [{ name: 'Home', path: '/' }]
  if (SERVICE_ROUTES.has(route)) {
    trail.push({ name: 'Services', path: '/services' })
    /* A child service gets its parent as a fourth level, which is the only
       place the Digital Marketing / Cybersecurity hierarchy is expressed -
       the URLs are flat. Guarded against a parent that is missing from
       SERVICE_ROUTES so a typo degrades to the old three-level trail rather
       than emitting a crumb that links nowhere. */
    const parent = SERVICE_PARENT[route]
    if (parent && parent !== route) trail.push({ name: crumbName(parent), path: parent })
  }
  else if (INDUSTRY_ROUTES.has(route)) trail.push({ name: 'Industries', path: '/industries' })
  else if (BLOG_ROUTES.has(route)) trail.push({ name: 'Blog', path: '/blog' })
  else if (NETSEC_PARENT[route]) {
    const parent = NETSEC_PARENT[route]
    trail.push({ name: crumbName(parent), path: parent })
  }
  trail.push({ name: crumbName(route), path: route })

  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: `https://pentabay.com${c.path === '/' ? '/' : c.path}`,
    })),
  }
  const el = existing || document.createElement('script')
  el.setAttribute('type', 'application/ld+json')
  el.setAttribute('data-breadcrumb', '')
  el.textContent = JSON.stringify(data)
  if (!existing) document.head.appendChild(el)
}

/* Fixed values for a route ROUTE_SEO/isKnownRoute doesn't recognise.
   Deliberately NOT `ROUTE_SEO['/']` - that fallback (the bug this whole
   block replaces) made an unrecognised URL indistinguishable from the
   homepage to a search engine: same title, same description, same
   canonical, all pointing at '/'. src/NotFound.jsx renders the actual page
   content for this case and used to set document.title itself too; that's
   now owned here instead; so the two can't race and disagree. */
const NOT_FOUND_SEO = {
  title: '404, Route Not Found | Pentabay',
  description: 'The page you were looking for doesn’t exist or may have moved.',
}

export default function RouteSEO() {
  useEffect(() => {
    function apply() {
      const route = currentRoute()

      if (!isKnownRoute(route)) {
        document.title = NOT_FOUND_SEO.title
        ensureMeta('meta[name="description"]', 'name', 'description', NOT_FOUND_SEO.description)
        ensureMeta('meta[name="robots"]', 'name', 'robots', 'noindex, follow')
        ensureMeta('meta[property="og:title"]', 'property', 'og:title', NOT_FOUND_SEO.title)
        ensureMeta('meta[property="og:description"]', 'property', 'og:description', NOT_FOUND_SEO.description)
        // No og:url/twitter equivalents either - same reasoning as the
        // canonical removal below, there is no URL this page represents.
        document.querySelector('meta[property="og:url"]')?.remove()
        ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', NOT_FOUND_SEO.title)
        ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', NOT_FOUND_SEO.description)
        removeCanonical()
        ensureFavicon(DEFAULT_FAVICON)
        // Breadcrumbs/Service schema both imply a real page hierarchy -
        // ensureServiceSchema already no-ops+removes for an unmatched route
        // (SERVICE_SCHEMA[route] is undefined), and ensureBreadcrumbs gets
        // the same treatment explicitly below rather than being called with
        // a route it was never designed to describe.
        document.querySelector('script[data-breadcrumb]')?.remove()
        ensureServiceSchema(route)

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'page_view', {
            page_title: NOT_FOUND_SEO.title,
            page_location: window.location.href,
          })
        }
        return
      }

      const seo = ROUTE_SEO[route] || ROUTE_SEO['/']
      const canonicalPath = route === '/' ? '/' : route
      const canonicalUrl = `https://pentabay.com${canonicalPath}`

      document.title = seo.title
      ensureMeta('meta[name="description"]', 'name', 'description', seo.description)
      // Matches index.html's static default so every other route keeps its
      // rich-result directives; only a route flagged `noindex` downgrades it.
      ensureMeta(
        'meta[name="robots"]',
        'name',
        'robots',
        seo.noindex
          ? 'noindex, follow'
          : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      )
      ensureMeta('meta[property="og:title"]', 'property', 'og:title', seo.title)
      ensureMeta('meta[property="og:description"]', 'property', 'og:description', seo.description)
      ensureMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl)
      ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title)
      ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description)
      ensureCanonical(canonicalUrl)
      ensureFavicon(seo.favicon || DEFAULT_FAVICON)
      ensureBreadcrumbs(route)
      ensureServiceSchema(route)

      // GA4 (cookieless): fire the page_view here, AFTER document.title is
      // set, so every SPA navigation is tracked with the right page_title.
      // index.html's gtag config uses send_page_view:false, so this is the
      // single source of page_views (fires on first load and each route
      // change). Guarded so a blocked/absent analytics script is harmless.
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_title: seo.title,
          page_location: window.location.href,
        })
      }
    }

    apply()
    window.addEventListener('hashchange', apply)
    window.addEventListener('popstate', apply)
    // The inline router navigates via history.pushState (which fires
    // neither hashchange nor popstate) and stamps <html data-route> on
    // every route change - observe that attribute so per-route SEO stays
    // in sync during client-side navigation.
    const mo = new MutationObserver(apply)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    return () => {
      window.removeEventListener('hashchange', apply)
      window.removeEventListener('popstate', apply)
      mo.disconnect()
    }
  }, [])
  return null
}
